import React, { useEffect, useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../util/reactIntl';
import { NamedLink } from '../../components';
import { isListingOnline, listingHasAvailableNow } from '../../util/presence';
import usePresenceMap from '../../util/usePresenceMap';
import { setListingAvailable } from '../../util/api';

import css from './AvailableToListenToggle.module.css';

/**
 * Available = in your scheduled hours AND present (Sam, Oct 29 2026).
 * Clock out leaves Available without changing hours.
 * No switch / Snooze — earners change hours on AvailabilityPage.
 */
const AvailableToListenToggle = props => {
  const {
    listing,
    online: onlineProp,
    onShownChange,
    className,
    variant = 'plain',
  } = props;
  const listingId = listing?.id?.uuid || (typeof listing?.id === 'string' ? listing.id : null);
  const { map } = usePresenceMap(listingId ? [listingId] : []);
  // Schedule = window they CAN be Available; live presence = they ARE (Sam, Oct 29 ~8 PM).
  const inScheduleHours = listingHasAvailableNow(listing);
  const liveOnline = isListingOnline(listingId, map, listing);
  const [override, setOverride] = useState(null);
  const [busy, setBusy] = useState(false);
  const baseShown = typeof onlineProp === 'boolean' ? onlineProp : liveOnline;
  const shown = override === null ? baseShown : override && inScheduleHours;

  useEffect(() => {
    setOverride(null);
  }, [baseShown]);

  const clock = on => {
    if (busy || !listingId) return;
    setBusy(true);
    setListingAvailable(listingId, on, { reason: 'user' })
      .then(() => setOverride(on))
      .catch(() => {})
      .then(() => setBusy(false));
  };

  useEffect(() => {
    if (typeof onShownChange === 'function') {
      onShownChange(!!shown);
    }
  }, [shown, onShownChange]);

  if (!listingId) {
    return null;
  }

  return (
    <div
      className={classNames(css.root, className, {
        [css.rootLabeled]: variant === 'labeled',
        [css.rootCompact]: variant === 'compact',
      })}
      data-available-status-root={shown ? 'on' : 'off'}
      data-available-toggle-variant={variant}
      data-available-calendar-only="true"
    >
      <p className={css.statusText} aria-live="polite" data-available-status={shown ? 'on' : 'off'}>
        {shown ? (
          <FormattedMessage id="DashboardPage.availablePillOn" />
        ) : (
          <FormattedMessage id="DashboardPage.availablePillOff" />
        )}
      </p>
      {variant === 'labeled' && shown ? (
        <button
          type="button"
          className={css.clockButton}
          onClick={() => clock(false)}
          disabled={busy}
          data-clock-out="true"
        >
          <FormattedMessage id="DashboardPage.clockOut" defaultMessage="Clock out" />
        </button>
      ) : null}
      {variant === 'labeled' && !shown && inScheduleHours ? (
        <>
          <p className={css.calendarHint} data-clocked-out-hint="true">
            <FormattedMessage
              id="DashboardPage.clockedOutHint"
              defaultMessage="Clocked out — open the site (or Clock in) during your hours to be Available again."
            />
          </p>
          <button
            type="button"
            className={css.clockButton}
            onClick={() => clock(true)}
            disabled={busy}
            data-clock-in="true"
          >
            <FormattedMessage id="DashboardPage.clockIn" defaultMessage="Clock in" />
          </button>
        </>
      ) : null}
      {variant === 'labeled' ? (
        <p className={css.calendarHint}>
          <NamedLink name="AvailabilityPage">
            <FormattedMessage id="DashboardPage.availableViaCalendar" defaultMessage="You set your hours. During them, be present — if you step away, clock out. Change hours below" />
          </NamedLink>
        </p>
      ) : null}
    </div>
  );
};

export default AvailableToListenToggle;
